import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Stack,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Snackbar,
} from '@mui/material';
import { getVolunteerByAccessToken, updateVolunteerProfile, getPublicVolunteerRegistrations } from '../services/api';
import { Volunteer, Registration } from '../models/types';
import { format } from 'date-fns';

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  
  const [volunteer, setVolunteer] = useState<Volunteer | null>(null);
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({
    name: '',
    phoneNumber: '',
    commodity: '',
    email: '',  // Added email field
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        const volunteerData = await getVolunteerByAccessToken('');
        setVolunteer(volunteerData);
        setEditForm({
          name: volunteerData.name || '',
          phoneNumber: volunteerData.phoneNumber || '',
          commodity: volunteerData.commodity || '',
          email: volunteerData.email || '',  // Added email initialization
        });
        
        const registrationsData = await getPublicVolunteerRegistrations(volunteerData.id);
        setRegistrations(registrationsData);
        
      } catch (err) {
        console.error('Error fetching profile details:', err);
        setError('Failed to load your profile. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []); 

  const handleStartEditing = () => {
    if (!volunteer) return;
    
    setEditForm({
      name: volunteer.name || '',
      phoneNumber: volunteer.phoneNumber || '',
      commodity: volunteer.commodity || '',
      email: volunteer.email || '',  // Added email initialization
    });
    setIsEditing(true);
  };

  const handleCancelEditing = () => {
    setIsEditing(false);
  };

  const handleFormChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setEditForm({ ...editForm, [name]: value });
  };

  const handleCommodityChange = (event: SelectChangeEvent) => {
    setEditForm({ ...editForm, commodity: event.target.value });
  };

  const handleSaveProfile = async () => {
    if (!volunteer) return;
    
    try {
      setIsSaving(true);
      const updatedVolunteer = await updateVolunteerProfile({
        name: editForm.name,
        phoneNumber: editForm.phoneNumber || undefined,
        commodity: editForm.commodity || undefined,
      });
      
      setVolunteer(updatedVolunteer);
      setIsEditing(false);
      setSuccessMessage('Dein Profil wurde erfolgreich aktualisiert');
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Fehler beim Aktualisieren deines Profils.');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Lade Profil...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Button variant="contained" onClick={() => navigate('/')}>
            Zurück zur Startseite
          </Button>
        </Box>
      </Container>
    );
  }

  if (!volunteer) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">Profil nicht gefunden. Bitte melde dich erneut an.</Alert>
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Button variant="contained" onClick={() => navigate('/login')}>
            Zur Anmeldung
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" gutterBottom>
            Mein Profil
          </Typography>
          {!isEditing && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleStartEditing}
            >
              Profil bearbeiten
            </Button>
          )}
        </Box>

        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Persönliche Informationen
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          {isEditing ? (
            <Box component="form" noValidate sx={{ mt: 2 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="name"
                label="Name"
                name="name"
                autoComplete="name"
                value={editForm.name}
                onChange={handleFormChange}
                sx={{ mb: 2 }}
              />
              
              <TextField
                margin="normal"
                fullWidth
                id="phoneNumber"
                label="Telefonnummer"
                name="phoneNumber"
                autoComplete="tel"
                value={editForm.phoneNumber}
                onChange={handleFormChange}
                sx={{ mb: 2 }}
              />
              
              <TextField
                margin="normal"
                fullWidth
                id="email"
                label="Email"
                name="email"
                type="email"
                autoComplete="email"
                value={editForm.email}
                disabled
                sx={{ mb: 2 }}
              />
              
              <FormControl fullWidth margin="normal" sx={{ mb: 2 }}>
                <InputLabel id="commodity-select-label">Organisation</InputLabel>
                <Select
                  labelId="commodity-select-label"
                  id="commodity-select"
                  value={editForm.commodity}
                  label="Organisation"
                  onChange={handleCommodityChange}
                >
                  <MenuItem value="">Keine Organisation</MenuItem>
                  <MenuItem value="Musikverein">Musikverein</MenuItem>
                  <MenuItem value="Kameradschaftsbund">Kameradschaftsbund</MenuItem>
                  <MenuItem value="Feuerwehr">Feuerwehr</MenuItem>
                  <MenuItem value="Landjugend">Landjugend</MenuItem>
                  <MenuItem value="Sonstige">Sonstige</MenuItem>
                </Select>
              </FormControl>
              
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button 
                  onClick={handleCancelEditing}
                  disabled={isSaving}
                >
                  Abbrechen
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSaveProfile}
                  disabled={isSaving || !editForm.name}
                >
                  {isSaving ? 'Speichern...' : 'Speichern'}
                </Button>
              </Box>
            </Box>
          ) : (
            <Stack spacing={2}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  <strong>Name:</strong> {volunteer.name}
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="body2" color="text.secondary">
                  <strong>E-Mail:</strong> {volunteer.email}
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="body2" color="text.secondary">
                  <strong>Telefon:</strong> {volunteer.phoneNumber || 'Nicht angegeben'}
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="body2" color="text.secondary">
                  <strong>Organisation:</strong> {volunteer.commodity || 'Nicht angegeben'}
                </Typography>
              </Box>
            </Stack>
          )}
        </Paper>

        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Meine Statistik
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                <strong>Gesamte Arbeitszeit:</strong> {volunteer.totalMinutesWorked || 0} Minuten
              </Typography>
            </Box>
            
           
          </Stack>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Meine Anmeldungen
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          {registrations.length === 0 ? (
            <Alert severity="info">
              Du hast dich noch für keine Jobs angemeldet.
            </Alert>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(224, 224, 224, 1)' }}>
                    <th style={{ padding: '16px 8px', textAlign: 'left' }}>Job</th>
                    <th style={{ padding: '16px 8px', textAlign: 'left' }}>Zeitfenster</th>
                    <th style={{ padding: '16px 8px', textAlign: 'left' }}>Status</th>
                    <th style={{ padding: '16px 8px', textAlign: 'left' }}>Gearbeitete Minuten</th>
                  </tr>
                </thead>
                <tbody>
                  {registrations.map((registration) => (
                    <tr key={registration.id} style={{ borderBottom: '1px solid rgba(224, 224, 224, 0.5)' }}>
                      <td style={{ padding: '16px 8px' }}>{registration.jobName}</td>
                      <td style={{ padding: '16px 8px' }}>
                        {registration.timeslotStart ? 
                          format(new Date(registration.timeslotStart), 'MMM d, HH:mm') : 
                          'Unknown'}
                      </td>
                      <td style={{ padding: '16px 8px' }}>
                        {registration.hasAttended ? (
                          <Chip label="Anwesend" color="success" size="small" />
                        ) : registration.isConfirmed ? (
                          <Chip label="Bestätigt" color="primary" size="small" />
                        ) : (
                          <Chip label="Ausstehend" color="warning" size="small" />
                        )}
                      </td>
                      <td style={{ padding: '16px 8px' }}>
                        {registration.hasAttended 
                          ? (registration.actualMinutesWorked || 'Nicht erfasst')
                          : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          )}
        </Paper>
      </Box>

      {/* Success message notification */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
        message={successMessage}
      />
    </Container>
  );
};

export default ProfilePage;
